import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import { provideBrowserGlobalErrorListeners } from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';

import { API_BASE_URL } from '@ebt/shared-api';
import { AuthAwarePreloadStrategy, authInterceptor, provideEbtAuth } from '@ebt/shared-auth';
import { provideEbtIcons } from '@ebt/shared-ui';

import { environment } from '../environments/environment';
import { routes } from './app.routes';

/**
 * Root provider config for the customer portal.
 *
 * Uses `portalApiBaseUrl` so every shared-api call hits the
 * `/api/v1/portal/*` surface, and configures `shared-auth` with the
 * portal-specific endpoints + audience='customer'. The interceptor is
 * the same one the staff app uses — it just reads from the
 * audience-tagged `AUTH_CONFIG` to know where to refresh.
 *
 * The router features mirror the CRM app's: background preloading of
 * lazy page chunks once signed in (gated on auth so anonymous visitors
 * don't download the portal), plus scroll reset on forward navigation
 * and restore on Back. See `crm-internal/src/app/app.config.ts` for the
 * reasoning.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withPreloading(AuthAwarePreloadStrategy),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    provideEbtIcons(),
    { provide: API_BASE_URL, useValue: environment.portalApiBaseUrl },
    provideEbtAuth({
      audience: 'customer',
      loginPath: `${environment.portalApiBaseUrl}/auth/login`,
      refreshPath: `${environment.portalApiBaseUrl}/auth/refresh`,
      logoutPath: `${environment.portalApiBaseUrl}/auth/logout`,
      mePath: `${environment.portalApiBaseUrl}/auth/me`,
      loginRoute: '/login',
      postLoginRoute: '/',
    }),
  ],
};
